// @flow
import {CommonActions} from '@react-navigation/native';
import {Linking, Platform} from 'react-native';
import {eventChannel} from 'redux-saga';
import {all, call, put, select, take, takeEvery, delay} from 'redux-saga/effects';
import {
  billingAccountsOuidSelector,
  customPaymentMeanSelector,
  customerOuidSelector,
  customerSelector,
} from '../Customer/selectors';
import type {PaymentActionType} from './actions';
import {
  clearStripeTimeout,
  fetchPaymentsSuccess,
  setIsThreeDSecureAuthentModalOpen,
  setPaymentAuthenticationResult,
  setPaymentAuthentications,
  setPaymentBraintreeClientToken,
  setPaymentInfo,
  setPaymentStripeClientToken,
  setStripeRedirectUrl,
  stripeTimeout,
} from './actions';

import {subYears} from 'date-fns';
import {tripica} from '../../api';
import config from '../../config';
import HandleErrorService from '../../lib/handleError';
import Navigation from '../../lib/navigation';
import {isLoggedInSelector} from '../Authentication';
import {fetchCustomerSaga} from '../Customer/sagas';
import {setLoading} from '../LoadingStatus';
import {fetchTriPicaNotificationsSaga} from '../Notification/sagas';
import {trpcNotificationsSelector} from '../Notification/selectors';
import {triggerProductOrderPolling} from '../PollingOrder';
import {emailSelector, loginSelector} from '../SignUp/Login';
import type {PaymentInfoType, StripePaymentMethodType} from './reducer';
import {hasStripeTimeOutSelector, paymentMeanSelector} from './selector';
import WizzeeLog from '../../lib/wizzeeLog';

const STRIPE_3DS_BROWSER_CANCELLED = 'STRIPE_3DS_BROWSER_CANCELLED';
const STRIPE_3DS_AUTH_COMPLETE = 'STRIPE_3DS_AUTH_COMPLETE';


const getWebEventChannel = () => {
  let emitter;
  const channel = eventChannel(emit => {
    emitter = emit;
    const on3DSComplete = event => {
      if (event.data !== '3DS-authentication-complete') {
        return;
      }

      emit({type: STRIPE_3DS_AUTH_COMPLETE});
    };

    window.addEventListener('message', on3DSComplete, false);
    return () => window.removeEventListener('message', on3DSComplete, false);
  });
  return {channel, emitter};
};

export const getMobileEventChannel = () => {
  let emitter;
  const channel = eventChannel(emit => {
    emitter = emit;
    const on3DSComplete = ({url}) => {
      if (!url || !url.includes('PaymentAuthorizationResult')) {
        return;
      }

      emit({type: STRIPE_3DS_AUTH_COMPLETE});
    };

    const linking = Linking.addEventListener('url', on3DSComplete);
    return () => linking.remove();
  });
  return {channel, emitter};
};

export const mobileEventChannel = getMobileEventChannel();

const getWebBaseUrl = () => {
  const pathname = window.location.pathname;
  const basePath = pathname.substring(0, pathname.lastIndexOf('/'));
  return window.location.origin + basePath + '/';
};

export function* verifyStripeThreeDSecure(
  stripePaymentMethod: StripePaymentMethodType,
): Generator<*, *, *> {
  const stripeAlreadyTimeout = yield select(hasStripeTimeOutSelector);
  const {email} = yield select(loginSelector);

  try {
    const {stripeCustomerId, stripeIntentId} =
      yield select(paymentMeanSelector);

    const {id: stripePaymentMethodId} = stripePaymentMethod;

    var preferredNetwork;
    preferredNetwork = stripePaymentMethod.card.networks.preferred
      ? stripePaymentMethod.card.networks.preferred.toUpperCase()
      : undefined;

    const stripeRedirectUrl = `${getWebBaseUrl()}assets/stripe_redirection.html`;

    const response = yield call(
      [tripica, 'checkStripePaymentMean'],
      {email},
      {
        stripeCustomerId,
        stripeIntentId,
        stripePaymentMethodId,
        stripeRedirectUrl,
        preferredNetwork,
      },
    );

    WizzeeLog.log('>>> verifyStripeThreeDSecure response', response);

    if (!response.stripeRedirectUrl.includes('PaymentAuthorizationResult')) {
      yield call(authenticateStripeThreeDSecure, response.stripeRedirectUrl);
    }
  } catch (error) {
    if (
      Array.isArray(error?.errorCodeList) &&
      error.errorCodeList.includes('TIMEOUT') &&
      !stripeAlreadyTimeout
    ) {
      yield put(stripeTimeout());
      yield delay(2000);
      yield call(verifyStripeThreeDSecure, stripePaymentMethod);
    } else {
      if (stripeAlreadyTimeout) {
        yield put(clearStripeTimeout());
      }
      throw error;
    }
    WizzeeLog.warn(error);
  } finally {
    if (stripeAlreadyTimeout) {
      yield put(clearStripeTimeout());
    }
  }
}

export function* changePaymentMean(
  paymentInfo: PaymentInfoType,
): Generator<*, *, *> {
  yield put(setLoading('confirmPayment', true));
  try {
    if (paymentInfo.type === 'STRIPE' && Platform.OS === 'web') {
      // Web: backend confirms the intent and handles 3DS via iframe
      yield call(verifyStripeThreeDSecure, paymentInfo.stripePaymentMethod);
    }
    // Native: PaymentSheet already confirmed the intent and handled 3DS.
    // Store the payment method ID so paymentMeanSelector includes it in the API payload.
    if (paymentInfo.type === 'STRIPE' && Platform.OS !== 'web' && paymentInfo.stripePaymentMethod) {
      yield put(setPaymentInfo({
        type: 'STRIPE',
        stripePaymentMethodId: paymentInfo.stripePaymentMethod.id,
      }));
    }

    const customerOuid = yield select(customerOuidSelector);
    const currentCustomPaymentMean = yield select(customPaymentMeanSelector);
    const billingAccountsOuid = yield select(billingAccountsOuidSelector);
    const paymentMean = yield select(paymentMeanSelector);

    const response = yield call(
      [tripica, 'changePaymentMean'],
      customerOuid,
      currentCustomPaymentMean ? currentCustomPaymentMean.ouid : 0,
      paymentMean,
      billingAccountsOuid,
    );

    if (!response || !response.errorCodeList) {
      throw new Error('No response or errorCodeList');
    }
    if (response.status === 'VALIDATED') {
      const paymentOrder = yield call(
        triggerProductOrderPolling,
        response.uuid,
      );
      if (paymentOrder) {
        yield call(fetchCustomerSaga);
      } else {
        throw {
          message: 'CHANGE_PAYMENT_FAILED',
          handled: true,
        };
      }
    }
  } catch (e) {
    throw e; // error is handled by calling saga
  } finally {
    yield put(setLoading('confirmPayment', false));
  }
}

export function* addPaymentMeanDuringActivate(
  action: PaymentActionType,
): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_ADD_PAYMENT_MEAN_DURING_ACTIVATE') return; // for flow
  yield put(setLoading('confirmPayment', true));

  try {
    const {paymentInfo} = action.payload;

    if (paymentInfo.type === 'STRIPE') {
      yield call(changePaymentMean, paymentInfo);
    }

    Navigation.dispatch(
      CommonActions.navigate({
        name: 'confirmPlanDuringActivate',
      }),
    );
  } catch (e) {
    WizzeeLog.warn('[saga] addPaymentMeanDuringActivate', e);
    if (!e.handled) {
      HandleErrorService.showToastError(e);
    }
    yield call(initStripePaymentMeanSaga);
  } finally {
    yield put(setLoading('confirmPayment', false));
  }
}

export function* addPaymentMeanDuringOrderSim(
  action: PaymentActionType,
): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_ADD_PAYMENT_MEAN_DURING_ORDER_SIM') return; // for flow
  yield put(setLoading('confirmPayment', true));
  try {
    const {paymentInfo} = action.payload;

    if (paymentInfo.type === 'STRIPE') {
      yield call(changePaymentMean, paymentInfo);
    }
    Navigation.dispatch(
      CommonActions.navigate({
        name: 'confirmPlan',
      }),
    );
  } catch (e) {
    WizzeeLog.warn('[saga] addPaymentMeanDuringOrderSim', e);
    if (!e.handled) {
      HandleErrorService.showToastError(e);
    }
    yield call(initStripePaymentMeanSaga);
    return;
  } finally {
    yield put(setLoading('confirmPayment', false));
  }
}

export function* changePaymentMeanOnDashboard(
  action: PaymentActionType,
): Generator<*, *, *> {
  if (action.type !== 'CHANGE_PAYMENT_MEAN_ON_DASHBOARD') return; // for flow
  yield put(setLoading('confirmPayment', true));
  try {
    const {paymentInfo} = action.payload;
    if (paymentInfo.type === 'STRIPE') {
      yield call(changePaymentMean, paymentInfo);
    }
  } catch (e) {
    WizzeeLog.warn('[saga] changePaymentMeanOnDashboard', e);
    if (!e.handled) {
      HandleErrorService.showToastError(e);
    }
  } finally {
    yield put(setLoading('confirmPayment', false));
    yield call(Navigation.dispatch, CommonActions.goBack());
    if (action.payload.bringBackTo && action.payload.bringBackTo === 'payments') {
      yield call(Navigation.dispatch, CommonActions.goBack());
    } else {
      yield call(
        Navigation.dispatch,
        CommonActions.navigate({
          name: action.payload.bringBackTo
        }),
      );
    }
  }
}

export function* refreshPaymentDetailsDuringOrderSim(
  action: PaymentActionType,
): Generator<*, *, *> {
  yield call(refreshPaymentDetails, action, 'payment', true);
}

export function* refreshPaymentDetailsDuringActivate(
  action: PaymentActionType,
): Generator<*, *, *> {
  yield call(refreshPaymentDetails, action, 'paymentDuringActivate', false);
}

export function* refreshPaymentDetailsOnDashboard(
  action: PaymentActionType,
): Generator<*, *, *> {
  yield call(refreshPaymentDetails, action, 'newPaymentMean', 'payments', false);
}

export function* refreshPaymentDetails(
  action: PaymentActionType,
  redirectTo: string,
  bringBackTo: string,
  shouldVerifyOrderConsistency: boolean,
): Generator<*, *, *> {
  yield put(setLoading('refreshPaymentDetails', true));
  try {
    switch (config.paymentProvider) {
      case 'stripe':
        yield call(initStripe);
        break;
      default:
        throw new Error('Unknown payment provider :' + config.paymentProvider);
    }
    yield call(
      Navigation.dispatch,
      CommonActions.navigate({
        name: 'registerPaymentDetails',
        params: {redirectTo, shouldVerifyOrderConsistency, bringBackTo},
      }),
    );
  } catch (e) {
    WizzeeLog.warn('[saga] refreshPaymentDetails', e);
    HandleErrorService.showToastError(e);
  } finally {
    yield call(fetchCustomerSaga);
    yield put(setLoading('refreshPaymentDetails', false));
  }
}

export function* initStripePaymentMeanSaga(): Generator<*, *, *> {
  yield put(setLoading('initStripePaymentMean', true));
  try {
    const customer = yield select(customerSelector);
    const email = yield select(emailSelector);
    const login = customer.login || {email};
    const catalog = yield call([tripica, 'getCatalog']);
    const response = yield call(
      [tripica, 'initStripePaymentMean'],
      login,
      catalog.PAYMENT_MEAN,
      customer,
      0,
    );
    yield put(setPaymentInfo(response));
  } catch (error) {
    WizzeeLog.warn('[saga] initStripePaymentMeanSaga', error);
    HandleErrorService.showToastError(error);
  } finally {
    WizzeeLog.log(`>>> initStripePaymentMeanSaga() - OK`);
    yield put(setLoading('initStripePaymentMean', false));
  }
}

export function* initBraintree(): Generator<*, *, *> {
  const isLoggedIn = yield select(isLoggedInSelector);
  const braintreeClientToken = yield call(
    [tripica, 'getBraintreeClientToken'],
    isLoggedIn,
  );
  if (!braintreeClientToken) {
    throw new Error('The braintree authentication failed');
  }
  yield put(setPaymentBraintreeClientToken(braintreeClientToken));
}

export function* initStripe(): Generator<*, *, *> {
  const isLoggedIn = yield select(isLoggedInSelector);
  const stripeClientToken = yield call(
    [tripica, 'getStripeClientToken'],
    isLoggedIn,
  );
  yield put(setPaymentStripeClientToken(stripeClientToken));
}

export function* fetchPayments(): Generator<*, *, *> {
  yield put(setLoading('refreshPaymentsHistory', true));
  try {
    const now = Date.now();
    const beginDate = subYears(now, config.paymentTimelinePeriod).getTime();
    const payments = yield call(
      [tripica, 'getPayments'],
      'DUE_LETTERED',
      beginDate,
      now,
    );
    yield put(fetchPaymentsSuccess(payments));
  } catch (e) {
    HandleErrorService.showToastError(e);
  } finally {
    yield put(setLoading('refreshPaymentsHistory', false));
  }
}

export function* fetchPaymentAuthentications(
  action: PaymentActionType,
): Generator<*, *, *> {
  if (action.type !== 'FETCH_PAYMENT_AUTHENTICATIONS') return; // for flow

  yield put(setLoading('fetchPaymentAuthentications', true));

  try {
    const action = {
      type: 'FETCH_TRIPICA_NOTIFICATIONS',
      payload: {refresh: false, hideErrors: false},
    };
    yield call(fetchTriPicaNotificationsSaga, action);
    const trpcNotifications = yield select(trpcNotificationsSelector);

    const now = Date.now();

    yield all(
      trpcNotifications
        .map(notification => {
          if (
            notification.type === 'PAYMENT_AUTHENT_REQUIRED' &&
            notification.maxSendDate < now
          ) {
            return call([tripica, 'seeNotification'], notification.ouid);
          }
        })
        .filter(Boolean),
    );

    const paymentAuthentications = trpcNotifications
      .filter(
        notification =>
          notification.type === 'PAYMENT_AUTHENT_REQUIRED' &&
          notification.maxSendDate > now,
      )
      .map(notification => {
        const {
          amount,
          billingAccountOuid,
          primaryResourceValue,
          processName,
          stripeIntentId,
          stripeRedirectUrl,
          subscriptionOuid,
          transactionId,
        } = notification.parameters;
        return {
          amount,
          billingAccountOuid,
          notificationMaxSendDate: notification.maxSendDate,
          notificationOuid: notification.ouid,
          primaryResourceValue,
          processName,
          stripeIntentId,
          stripeRedirectUrl,
          subscriptionOuid,
          transactionId,
        };
      });

    yield put(setPaymentAuthentications(paymentAuthentications));
  } catch (e) {
    WizzeeLog.warn('[saga] fetchPaymentAuthentications', e);
    HandleErrorService.showToastError(e);
  } finally {
    yield put(setLoading('fetchPaymentAuthentications', false));
  }
}

export function* checkPaymentAuthorizationResult(
  action: PaymentActionType,
): Generator<*, *, *> {
  if (action.type !== 'CHECK_PAYMENT_AUTHORIZATION_RESULT') return; // for flow
  yield put(setLoading('checkPaymentAuthorizationResult', true));

  const {notificationOuid, stripeIntentId, stripeRedirectUrl} = action.payload;
  try {
    const customer = yield select(customerSelector);
    const signUpEmail = yield select(emailSelector);
    const login = {email: customer?.login?.email || signUpEmail};

    let paymentAuthenticationResult = 'PENDING_AUTHENT';

    // 3DS authentication via WebView (native) or iframe (web).
    // authenticateStripeThreeDSecure handles platform differences internally.
    const authAction = yield call(
      authenticateStripeThreeDSecure,
      stripeRedirectUrl,
    );

    if (authAction && authAction.type === STRIPE_3DS_AUTH_COMPLETE) {
      const {stripeIntentStatus} = yield call(
        [tripica, 'checkStripePaymentMean'],
        login,
        {stripeIntentId},
      );

      paymentAuthenticationResult =
        stripeIntentStatus === 'succeeded' ? 'SUCCESS' : 'FAILURE';

      yield call([tripica, 'seeNotification'], notificationOuid);
    }

    yield put(setPaymentAuthenticationResult(paymentAuthenticationResult));
  } catch (e) {
    WizzeeLog.warn('[saga] checkPaymentAuthorizationResult', e);
    HandleErrorService.showToastError(e);
  } finally {
    yield put(setLoading('checkPaymentAuthorizationResult', false));
  }
}

export function* authenticateStripeThreeDSecure(
  stripeRedirectUrl: string,
): Generator<*, *, *> {
  try {
    let action;
    const {channel} =
      Platform.OS === 'web' ? getWebEventChannel() : mobileEventChannel;
    if (Platform.OS !== 'web') {
      yield put(setStripeRedirectUrl(stripeRedirectUrl));
      yield put(setIsThreeDSecureAuthentModalOpen(true));
    } else {
      const iframe = document.createElement('iframe');
      iframe.scrolling = 'auto';
      iframe.id = 'stripe-3DS';
      iframe.src = stripeRedirectUrl;
      iframe.style.position = 'absolute';
      iframe.style.top = '0px';
      iframe.width = window.innerWidth;
      iframe.height = window.innerHeight;

      const body = document.querySelector('body');
      if (body) {
        body.appendChild(iframe);
      } else {
        throw new Error('No body');
      }
    }

    while (true) {
      action = yield take(channel);
      if (
        [STRIPE_3DS_BROWSER_CANCELLED, STRIPE_3DS_AUTH_COMPLETE].includes(
          action.type,
        )
      ) {
        break;
      }
    }

    if (Platform.OS === 'web') {
      const stripe3DSIframe = document.querySelector('#stripe-3DS');
      const body = document.querySelector('body');
      if (stripe3DSIframe && body) {
        body.removeChild(stripe3DSIframe);
      }
    } else {
      yield put(setIsThreeDSecureAuthentModalOpen(false));
    }
    return action;
  } catch (error) {
    WizzeeLog.warn(error);
  }
}

export default function* (): Generator<*, *, *> {
  yield* [
    takeEvery('INIT_STRIPE_PAYMENT_MEAN', initStripePaymentMeanSaga),
    takeEvery(
      'SIGNUP_ADD_PAYMENT_MEAN_DURING_ORDER_SIM',
      addPaymentMeanDuringOrderSim,
    ),
    takeEvery(
      'SIGNUP_ADD_PAYMENT_MEAN_DURING_ACTIVATE',
      addPaymentMeanDuringActivate,
    ),
    takeEvery(
      'SIGNUP_REFRESH_PAYMENT_DURING_ORDER_SIM',
      refreshPaymentDetailsDuringOrderSim,
    ),
    takeEvery(
      'SIGNUP_REFRESH_PAYMENT_DURING_ACTIVATE',
      refreshPaymentDetailsDuringActivate,
    ),
    takeEvery(
      'REFRESH_PAYMENT_DETAILS_ON_DASHBOARD',
      refreshPaymentDetailsOnDashboard,
    ),
    takeEvery('CHANGE_PAYMENT_MEAN_ON_DASHBOARD', changePaymentMeanOnDashboard),
    takeEvery('FETCH_PAYMENTS', fetchPayments),
    takeEvery(
      'CHECK_PAYMENT_AUTHORIZATION_RESULT',
      checkPaymentAuthorizationResult,
    ),
    takeEvery('FETCH_PAYMENT_AUTHENTICATIONS', fetchPaymentAuthentications),
  ];
}
